// Per-browser "Add to Home Screen" guidance (#218). iOS gives web pages no way to trigger the install,
// and the menu path differs by browser app (Safari, Chrome, Edge, Firefox) — so we detect the browser and
// show the right steps. On Android/desktop the real install prompt (beforeinstallprompt) is used instead.

export type IosBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other';

/** True on iPhone/iPad (incl. iPadOS, which reports as a Mac with a touch screen). */
export function isIosDevice(): boolean {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** Which browser app is being used on iOS (they all run WebKit, but their menus differ). */
function iosBrowser(): IosBrowser {
  const ua = navigator.userAgent;
  if (/EdgiOS/i.test(ua)) return 'edge';
  if (/CriOS/i.test(ua)) return 'chrome';
  if (/FxiOS/i.test(ua)) return 'firefox';
  if (/Safari/i.test(ua)) return 'safari';
  return 'other';
}

export interface InstallGuide {
  /** The browser app name, for the heading ("In Edge on iPhone:"). */
  browser: string;
  /** A caveat shown above the steps (e.g. Firefox can't do it). */
  note?: string;
  /** The ordered steps to follow. */
  steps: string[];
}

/** The right Add-to-Home-Screen steps for the current iOS browser. */
export function iosInstallGuide(): InstallGuide {
  switch (iosBrowser()) {
    case 'edge':
      return {
        browser: 'Edge',
        steps: [
          'Tap the ••• menu in the bar at the bottom of the screen.',
          'Tap “Share”.',
          'Scroll down and tap “Add to Home Screen”, then tap “Add”.',
        ],
      };
    case 'chrome':
      return {
        browser: 'Chrome',
        steps: [
          'Tap the Share button (a square with an arrow pointing up) in the top address bar.',
          'Scroll down and tap “Add to Home Screen”.',
          'Tap “Add”.',
        ],
      };
    case 'firefox':
      return {
        browser: 'Firefox',
        note: 'Firefox on iPhone can’t add apps to the Home Screen.',
        steps: [
          `Open ${location.host} in Safari or Edge instead.`,
          'Then use that browser’s menu to add it to your Home Screen.',
        ],
      };
    case 'safari':
      return {
        browser: 'Safari',
        steps: [
          'Tap the Share button (a square with an arrow pointing up) in the bottom toolbar.',
          'Scroll down and tap “Add to Home Screen”.',
          'Tap “Add” in the top-right.',
        ],
      };
    default:
      return {
        browser: 'your browser',
        steps: ['Open your browser’s menu or Share button.', 'Choose “Add to Home Screen”.'],
      };
  }
}
